import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { portexColor, Status, TextInput } from "@portex-pro/ui-components";
import { Truck } from "api/rest/quote-requests";
import classNames from "classnames";
import EditableColumnList, { EditableColumnRowProps } from "components/EditableColumnList";
import Text from "components/Text";
import withAsync from "components/withAsync";
import { getFtlTrailerSize } from "pages/shipper/utils/getFtlTrailerSize";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { formatTrailerType } from "utils/formatTrailerType";

import { useEditableQuoteRequest } from "../../hooks/quotes/useEditableQuoteRequest";

const ReferencesContainer = withAsync({
  useHook: useEditableQuoteRequest,
  Component: ({
    loadedData: { quoteRequest, isEditing, setEditing, setQuoteRequest, setQuoteRequestByPath, save },
  }) => {
    const { t } = useTranslation(["shipper", "common", "shipments", "quoteDetails"]);

    return (
      <EditableColumnList
        title={t("shipments:shipmentDetails_poReference_title")}
        CollapseProps={{ timeout: 1000 + 250 * ((quoteRequest.load_spec?.trucks?.length ?? 0) / 10) }}
        isEditing={isEditing}
        setEditing={setEditing}
        disableEditing={quoteRequest.status === "BOOKED" || quoteRequest.status === "CLOSED"}
        onSave={() => save()}
        getCollapseCopy={(isOpen) => (
          <>
            <Trans
              ns="quoteDetails"
              i18nKey="poReferenceDetails_viewTrucksButton"
              count={quoteRequest.load_spec?.trucks?.length}
              values={{
                state: isOpen
                  ? t("quoteDetails:poReferenceDetails_viewTrucksButton_close")
                  : t("quoteDetails:poReferenceDetails_viewTrucksButton_open"),
              }}
              components={[
                <Status
                  light
                  rounded
                  style={{
                    margin: "0 0.5rem",
                    fontSize: "14px",
                    color: "white",
                    backgroundColor: portexColor.blue500,
                    width: "24px",
                    height: "24px",
                    padding: "4px",
                    borderRadius: "100%",
                    display: "inline-flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                ></Status>,
              ]}
            />
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </>
        )}
        rows={[
          {
            type: "string",
            value: quoteRequest.reference_number ?? "",
            label: t("quoteDetails:poReferenceDetails_shipment"),
            editableProps: { onChange: (e) => setQuoteRequest({ reference_number: e.target.value }) },
          },
          {
            type: "string",
            value: quoteRequest.internal_reference_number ?? "",
            label: t("shipper:shipmentDetailsStep.internalReferenceNumber"),
            editableProps: { onChange: (e) => setQuoteRequest({ internal_reference_number: e.target.value }) },
            hide: quoteRequest.mode === "FCL" || quoteRequest.mode === "AIR",
          },
          {
            type: "custom",
            label: t("shipper:shipmentDetailsStep.hideInternalRefTillDispatch"),
            value: (
              <input
                type="checkbox"
                disabled={!isEditing}
                checked={quoteRequest.hide_internal_ref_till_dispatch ?? false}
                onChange={(e) => setQuoteRequest({ hide_internal_ref_till_dispatch: e.target.checked })}
              />
            ),
            hide: quoteRequest.mode === "FCL" || quoteRequest.mode === "AIR",
          },
          {
            type: "custom",
            hide: quoteRequest.mode === "DRAYAGE",
            value: (
              <div className="flex gap-2 items-center w-full">
                <input
                  type="text"
                  disabled={!isEditing}
                  value={quoteRequest.load_spec?.trucks?.length ?? 0}
                  className={classNames(
                    "w-full text-center h-[42px] border border-border rounded-md"
                    // props.disabled && disabledClass
                  )}
                  onChange={(ev) => {
                    const currentTruckAmount = quoteRequest.load_spec?.trucks?.length ?? 0;
                    const amount = Number(ev.target.value);
                    if (amount > currentTruckAmount) {
                      const defaultTruckValue: Omit<Truck, "id"> = {
                        trailer_size: quoteRequest.load_spec?.trailer_size,
                        trailer_type: quoteRequest.load_spec?.trailer_type,
                      };

                      setQuoteRequest({
                        load_spec: {
                          trucks: [
                            ...(quoteRequest.load_spec?.trucks ?? []),
                            ...Array(amount - currentTruckAmount).fill(defaultTruckValue),
                          ],
                        },
                      });
                    } else {
                      setQuoteRequest({
                        load_spec: {
                          trucks: quoteRequest.load_spec?.trucks
                            ? quoteRequest.load_spec.trucks.slice(0, amount - currentTruckAmount || undefined)
                            : [],
                        },
                      });
                    }
                  }}
                />
                <button
                  type="button"
                  disabled={!isEditing}
                  className={"h-[42px] w-[42px] border border-border rounded-md"}
                  onClick={() =>
                    setQuoteRequest({
                      load_spec: {
                        trucks: [
                          ...(quoteRequest.load_spec?.trucks ?? []),
                          {
                            trailer_size: quoteRequest.load_spec?.trailer_size,
                            trailer_type: quoteRequest.load_spec?.trailer_type,
                          },
                        ],
                      },
                    })
                  }
                >
                  +
                </button>
              </div>
            ),
            label: t("quoteDetails:poReferenceDetails_numberOfTrucks"),
          },
          ...(quoteRequest.load_spec?.trucks?.map(
            (truck, index): EditableColumnRowProps => ({
              label: t("quoteDetails:poReferenceDetails_truckCount", {
                truckNumber: (quoteRequest.load_spec?.truck_quantity ?? 0) > 1 ? index + 1 : undefined,
              }),
              hide: quoteRequest.mode === "DRAYAGE",
              startCollapsableSection: true,
              subLabel: [getFtlTrailerSize(truck.trailer_size), formatTrailerType(truck.trailer_type)].join(" "),
              value: (
                <div className="w-full flex items-center space-x-3">
                  <Text
                    size="small"
                    weight="bold"
                    // @ts-expect-error typographyProps is typed incorrectly and doesn't think textWrap is an allowed style
                    typographyProps={{ style: { display: "inline", textWrap: "nowrap " } }}
                  >
                    {t("quoteDetails:poReferenceDetails_truckPoNumber")}
                  </Text>
                  <TextInput
                    fullWidth
                    value={truck.reference_number}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setQuoteRequestByPath({
                        path: `load_spec.trucks.${index}.reference_number`,
                        value: e.target.value,
                      })
                    }
                  />
                  {(quoteRequest.load_spec?.trucks?.length ?? 0) > 1 && (
                    <button
                      type="button"
                      disabled={!isEditing}
                      className={"h-[42px] w-[42px] border border-border rounded-md"}
                      onClick={() =>
                        setQuoteRequest({
                          load_spec: { trucks: quoteRequest.load_spec?.trucks?.filter((_, i) => i !== index) },
                        })
                      }
                    >
                      -
                    </button>
                  )}
                </div>
              ),
              type: "custom",
            })
          ) ?? []),
        ]}
      />
    );
  },
});

export default ReferencesContainer;
